import type { ColumnDef, FilterValue, QuickFilter, SortState } from './types'
import { isUnmeasured } from './comparators'

/**
 * Accent- and case-folded, for comparison only.
 *
 * `sensitivity: 'base'` in `compareText` does this for the sort; this is the
 * same rule for the filter, so a column cannot say two values are equal when
 * you sort it and different when you search it.
 */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLocaleLowerCase('es-MX')
}

/** The value a filter matches against: `filterValue` if the column has one. */
function keyOf<R>(c: ColumnDef<R>, row: R): unknown {
  return c.filterValue ? c.filterValue(row) : c.value(row)
}

/**
 * Does one row pass one column's filter?
 *
 * ── THE NULL POLICY IS CHECKED FIRST, ALWAYS ────────────────────────────────
 * An unmeasured value is not a value that happens to fail the predicate. If it
 * were folded in as `0` or `''` it would pass a `min: 0` range and fail a text
 * search, which are two different lies about the same absence. So the policy
 * decides before the predicate runs, and it is visible in the popover and in
 * the URL rather than being a default nobody can see.
 */
export function passes<R>(c: ColumnDef<R>, f: FilterValue, row: R): boolean {
  const v = keyOf(c, row)
  const unmeasured = isUnmeasured(v)

  if (f.nulls === 'only') return unmeasured
  if (unmeasured) return f.nulls === 'include'

  switch (f.kind) {
    case 'text': {
      // ── FOLDED ON BOTH SIDES, TO MATCH THE COLLATOR ────────────────────
      // `compareText` uses `sensitivity: 'base'`, so the SORT already treats
      // «Mexico» and «México» as one value. A plain `includes` did not, so a
      // search for `mexico` hid the row the sort had just told you was the same
      // one. This data is Spanish and typed by hand: accents are the norm.
      const q = fold(f.q)
      if (q === '') return true
      return fold(String(v)).includes(q)
    }
    case 'multi':
      return f.picked.length === 0 || f.picked.includes(String(v))
    case 'range': {
      const n = Number(v)
      if (!Number.isFinite(n)) return false
      // A hand-edited URL can carry a non-numeric bound. `NaN` compares false
      // against everything, so the bound is IGNORED rather than hiding every
      // row — a broken link should degrade to no filter, not to an empty table.
      if (f.min !== null && Number.isFinite(f.min) && n < f.min) return false
      if (f.max !== null && Number.isFinite(f.max) && n > f.max) return false
      return true
    }
    case 'dates': {
      // Compared as ISO strings, for the same reason `compareDate` is: parsing
      // through a local `Date` would move a boundary by up to a day.
      const s = String(v).slice(0, 10)
      if (f.from !== null && s < f.from) return false
      if (f.to !== null && s > f.to) return false
      return true
    }
    case 'bool':
      return Boolean(v) === f.want
  }
}

/** True when a filter would hide nothing, so it can be dropped from the URL. */
export function isInert(f: FilterValue): boolean {
  if (f.nulls !== 'exclude') return false
  switch (f.kind) {
    case 'text':
      return f.q.trim() === ''
    case 'multi':
      return f.picked.length === 0
    case 'range':
      return f.min === null && f.max === null
    case 'dates':
      return f.from === null && f.to === null
    case 'bool':
      return false
  }
}

/**
 * The distinct values in a column, with counts, for a `multi` filter.
 *
 * Derived from the ROWS rather than declared, because several of these domains
 * are facts about the product's database — a membership role, a Stripe status —
 * that this repository does not get to enumerate. Unmeasured rows are counted
 * separately so the popover can offer them as an explicit choice instead of
 * pretending they are a value.
 */
export function facets<R>(
  c: ColumnDef<R>,
  rows: R[],
): { options: { value: string; n: number }[]; unmeasured: number } {
  const counts = new Map<string, number>()
  let unmeasured = 0
  for (const r of rows) {
    const v = keyOf(c, r)
    if (isUnmeasured(v)) unmeasured += 1
    else {
      const k = String(v)
      counts.set(k, (counts.get(k) ?? 0) + 1)
    }
  }
  const options = [...counts.entries()]
    .map(([value, n]) => ({ value, n }))
    // Frequency, then the label, so the list is stable across refetches.
    .sort((a, b) => b.n - a.n || a.value.localeCompare(b.value))
  return { options, unmeasured }
}

export interface TableInput<R> {
  rows: R[]
  columns: ColumnDef<R>[]
  filters: Record<string, FilterValue>
  quick: string[]
  quickFilters?: QuickFilter<R>[]
  sort: SortState
  /** The order the screen was designed to be read in. */
  defaultOrder?: (a: R, b: R) => number
}

/**
 * Apply the quick filters, the column filters and the sort.
 *
 * Returns the input array BY REFERENCE when nothing is active and there is no
 * default order, so an untouched view stays referentially stable for the
 * virtualizer and for React's memo boundaries.
 */
export function applyTable<R>(input: TableInput<R>): { visible: R[]; hidden: number } {
  const { rows, columns, filters, quick, quickFilters = [], sort, defaultOrder } = input

  const activeQuick = quickFilters.filter((q) => quick.includes(q.id))
  const activeCols = columns
    .map((c) => [c, filters[c.id]] as const)
    .filter((pair): pair is readonly [ColumnDef<R>, FilterValue] => Boolean(pair[1]))
    // A URL can name a filter KIND the column does not offer — a link shared
    // before a column changed its type, or a hand edit. `passes` would then
    // evaluate a text predicate against a range column and hide everything, so
    // the mismatched filter is dropped instead: a stale link degrades to the
    // unfiltered table rather than to an empty one.
    .filter(([c, f]) => c.filter === f.kind)
    .filter(([, f]) => !isInert(f))

  const noFilter = activeQuick.length === 0 && activeCols.length === 0
  if (noFilter && sort.kind === 'default' && !defaultOrder) {
    return { visible: rows, hidden: 0 }
  }

  const visible = noFilter
    ? rows
    : rows.filter(
        (r) => activeQuick.every((q) => q.test(r)) && activeCols.every(([c, f]) => passes(c, f, r)),
      )

  const ordered = sortRows(visible, columns, sort, defaultOrder)
  return { visible: ordered, hidden: rows.length - visible.length }
}

function sortRows<R>(
  rows: R[],
  columns: ColumnDef<R>[],
  sort: SortState,
  defaultOrder?: (a: R, b: R) => number,
): R[] {
  if (sort.kind === 'default') {
    return defaultOrder ? [...rows].sort(defaultOrder) : rows
  }
  const c = columns.find((x) => x.id === sort.columnId)
  // Also when the column exists but has no order: a pasted URL naming one must
  // fall back to the designed order rather than to an arbitrary tie.
  if (!c || c.sortable === false) return defaultOrder ? [...rows].sort(defaultOrder) : rows

  // `dir` goes INTO the comparator, not around it: the null rule sinks
  // unmeasured rows in both directions, so a negation applied afterwards would
  // float them to the top of a descending sort. See `withNullsLast`.
  const dir = sort.direction
  // A stable secondary order so equal values do not reshuffle between renders.
  // Array.prototype.sort is stable in every engine this app targets, so
  // preserving the incoming order for ties is enough.
  return [...rows].sort((ra, rb) => c.compare(c.value(ra), c.value(rb), dir))
}
